/**
 * プライバシーポリシー。
 *
 * 実装と食い違わせないため、次は実際の挙動をそのまま書いている。
 * 挙動を変えたときは、ここも直すこと。
 *
 *   - アクセスログには共有リンクが含まれる (apps/api/src/app.ts のロガー設定)
 *   - 確認コードはハッシュ化して保存し、10 分で失効・24 時間後に削除
 *     (apps/api/src/services/auth-service.ts)
 *   - 保存先は AWS 東京リージョン、ログの保持は 30 日
 *     (terraform/modules/api)
 *
 * 受取先 (口座 / PayPay ID) の説明は、機能が既定で無効なためコメントアウトしてある。
 * PAYOUT_ACCOUNTS_ENABLED=true にする前に必ず戻すこと。戻さないと、
 * 口座情報を取得しているのにポリシーに書いていない状態になる。
 *
 * 支出名をカテゴリ判定へ送ることの記載 (委託先の TypeSafe AI) は、
 * 実際に送っているかどうかに関わらず載せている。詳細は docs/legal.md。
 */
import { LegalDocument, LegalSection } from "../components/LegalDocument.js";

export function PrivacyPage() {
  return (
    <LegalDocument title="プライバシーポリシー" updatedOn="2026年8月18日">
      <p>
        Applest (以下「当方」) は、割り勘計算サービス「スプリ (Spli)」 (以下「本サービス」)
        における個人情報および利用者情報の取り扱いについて、次のとおり定めます。
      </p>

      <LegalSection heading="1. 取得する情報">
        <p>本サービスは、次の情報を取得します。</p>

        <h3 className="legal__subheading">(1) 利用者が入力する情報</h3>
        <ol>
          <li>グループ名・説明</li>
          <li>
            メンバーの名前 (本名である必要はありません。ニックネームでも利用できます)
          </li>
          <li>支出の内容 (品目名・カテゴリ・金額・日付・メモ)</li>
          <li>精算の制約 (誰と誰の間で送金するか、誰が誰の分を負担するか)</li>
          <li>精算の記録 (送金した / 受け取った の記録とメモ)</li>
        </ol>
        <p className="legal__note">
          これらは会員登録をしなくても入力できます。当方はこれらの情報から個人を特定することを
          目的としていません。
        </p>

        <h3 className="legal__subheading">(2) アカウントを作成した場合</h3>
        <ol>
          <li>メールアドレス</li>
          <li>
            表示名 (メールアドレスから自動的に作られます。利用者が入力するものではありません)
          </li>
          <li>
            ログイン用の確認コード。コードそのものは保存せず、復元できない形 (ハッシュ値) で
            保存します
          </li>
        </ol>

        {/* <h3 className="legal__subheading">(3) 受取先を登録した場合</h3>
        <p>
          送金の受取先を登録する機能を有効にしている場合に限り、次の情報を取得します。この機能は
          既定では無効です。
        </p>
        <ol>
          <li>金融機関名・支店名・口座種別・口座番号・口座名義</li>
          <li>PayPay ID などの受取先の識別情報</li>
          <li>受取先に関するメモ</li>
        </ol>
        <p className="legal__note">
          <strong>
            登録した受取先は、紐づけたグループの共有リンクを知っている人に表示されます。
          </strong>
          他人に見せたくない場合は登録しないでください。登録後もいつでも削除できます。
        </p> */}

        <h3 className="legal__subheading">(3) 自動的に取得する情報</h3>
        <ol>
          <li>
            IP アドレス、ブラウザの種類 (ユーザーエージェント)、アクセス日時、アクセスしたページの URL
          </li>
          <li>Cookie および類似の技術によって付与される識別子</li>
        </ol>

        <h3 className="legal__subheading">(4) 利用者の端末に保存する情報</h3>
        <p>
          次の情報は、当方のサーバーではなく利用者の端末内 (ブラウザ) に保存されます。
          ブラウザのデータを削除すれば消去できます。
        </p>
        <ol>
          <li>ログイン状態を保持するためのトークン</li>
          <li>最近開いたグループの共有リンクと名前 (履歴の表示に使います)</li>
        </ol>
      </LegalSection>

      <LegalSection heading="2. 利用目的">
        <ol>
          <li>本サービスの提供・維持・改善のため</li>
          <li>割り勘の計算と精算結果の表示のため</li>
          <li>アカウントの認証、および参加したグループの履歴を表示するため</li>
          <li>不正な利用の防止、および障害の検知と原因調査のため</li>
          <li>広告の配信のため</li>
          <li>お問い合わせへの対応のため</li>
        </ol>
      </LegalSection>

      <LegalSection heading="3. 第三者への提供">
        <p>
          当方は、次の場合を除き、取得した個人情報を第三者に提供しません。利用者の個人情報を販売する
          ことはありません。
        </p>
        <ol>
          <li>利用者の同意がある場合</li>
          <li>法令に基づく場合、または裁判所・行政機関から適法に開示を求められた場合</li>
          <li>人の生命・身体・財産の保護のために必要で、利用者の同意を得ることが難しい場合</li>
          <li>次項に定める業務委託先に、必要な範囲で取り扱いを委託する場合</li>
        </ol>
      </LegalSection>

      <LegalSection heading="4. 業務の委託 (外部サービスの利用)">
        <p>本サービスは、提供のために次の外部サービスを利用しています。</p>
        <ol>
          <li>
            <strong>Amazon Web Services (AWS)</strong> — サーバーおよびデータベース。データは
            東京リージョン (日本国内) に保存されます
          </li>
          <li>
            <strong>Amazon SES</strong> — ログイン用の確認コードの送信
          </li>
          <li>
            <strong>Google Analytics</strong> — 利用状況の分析 (次項)
          </li>
          <li>
            <strong>Google AdSense / Google AdMob</strong> — 広告の配信 (次項)
          </li>
          <li>
            <strong>TypeSafe AI, Inc. (米国)</strong> — 支出名からカテゴリ (食事・交通など) を推定するための判定。
          </li>
        </ol>
      </LegalSection>

      <LegalSection heading="5. アクセス解析・広告と Cookie">
        <ol>
          <li>
            本サービスは、利用状況を把握して改善に役立てるために Google Analytics を利用しています。
            Cookie によって付与される識別子や、アクセスしたページ・日時などの情報が Google へ
            送信されます。氏名やメールアドレスは送信していません。
          </li>
          <li>
            Google Analytics による計測は、
            <a
              href="https://tools.google.com/dlpage/gaoptout?hl=ja"
              target="_blank"
              rel="noreferrer noopener"
            >
              Google アナリティクス オプトアウト アドオン
            </a>
            で無効にできます。
          </li>
          <li>
            本サービスは、運営費用をまかなうために Google およびその提携先による広告を掲載する場合が
            あります。
          </li>
          <li>
            これらの第三者は、利用者の興味に応じた広告を表示するために、Cookie や広告識別子を
            利用することがあります。
          </li>
          <li>
            パーソナライズ広告は、
            <a href="https://myadcenter.google.com/" target="_blank" rel="noreferrer noopener">
              Google の広告設定
            </a>
            から無効にできます。ブラウザの設定で Cookie を無効にすることもできますが、その場合
            本サービスの一部の機能が使えなくなることがあります。
          </li>
          <li>
            Google による情報の取り扱いについては、
            <a
              href="https://policies.google.com/technologies/ads"
              target="_blank"
              rel="noreferrer noopener"
            >
              Google の広告に関するポリシー
            </a>
            をご確認ください。
          </li>
        </ol>
      </LegalSection>

      <LegalSection heading="6. 保存期間">
        <ol>
          <li>
            グループ・メンバー・支出・精算の記録は、利用者が削除するまで保存します。長期間利用されて
            いないグループは、当方の判断で削除する場合があります。
          </li>
          <li>ログイン用の確認コードは、発行から 10 分で失効し、24 時間後に削除します。</li>
          <li>アクセスログは、取得から 30 日間保存したのち削除します。</li>
          <li>
            データベースのバックアップは、障害からの復旧のために最大 7 日間保持します。削除した
            データがこの期間だけバックアップに残ることがあります。
          </li>
        </ol>
      </LegalSection>

      <LegalSection heading="7. 開示・訂正・削除の請求">
        <ol>
          <li>
            利用者は、当方が保有する自身の個人情報について、開示・訂正・利用停止・削除を請求できます。
            末尾のお問い合わせ先までご連絡ください。
          </li>
          <li>
            ご本人であることを確認できない場合は、請求にお応えできないことがあります。
          </li>
          <li>
            アカウントを削除すると、メールアドレスと表示名は削除されます。
            グループに入力された内容 (メンバー名・支出など) は、他の参加者が引き続き利用するため
            残ります。
          </li>
        </ol>
      </LegalSection>

      <LegalSection heading="8. 未成年の利用">
        <p>
          未成年の方は、親権者などの法定代理人の同意を得たうえでご利用ください。同意を得ずに登録された
          場合、当方はその情報を削除することがあります。
        </p>
      </LegalSection>

      <LegalSection heading="9. 本ポリシーの変更">
        <p>
          当方は、必要に応じて本ポリシーを変更します。変更後の内容は、本サービス上に掲載した時点から
          適用されます。重要な変更を行う場合は、本サービス上でお知らせします。
        </p>
      </LegalSection>

      <LegalSection heading="10. お問い合わせ先">
        <p>
          本サービスの個人情報の取り扱いに関するお問い合わせは、<a href="https://docs.google.com/forms/d/e/1FAIpQLSfu1sfZqM-Ko_7uIwan-luH8MdBDoQSebi5zhVFicCX3juEsg/viewform?usp=publish-editor" target="_blank" rel="noreferrer noopener">お問い合わせフォーム</a> までご連絡ください。
        </p>
        <p className="legal__note">運営者: Applest</p>
      </LegalSection>
    </LegalDocument>
  );
}
